import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Guid } from 'guid-typescript';
import * as $ from 'jquery';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';
import { Category, ContentObject, Course, Team, TeamMember, UserCourse, UserDashboard } from '../app/app.models';
import { UserDetails } from '../app/learn/learn.models';
import { environment } from '../environments/environment';
import { ApplicationInsightsService } from './application-insights.service';
import { SummaryObject } from './report/report.models';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  public firstName: string = '';
  public lastName: string = '';
  public personId: Guid = Guid.createEmpty();
  public initials: string = '';
  public emailAddress: string = '';
  public mbtiType: string = '';
  public assessmentSiteBaseUrl: string = '';
  public loginSiteBaseUrl: string = '';
  public marketingSiteBaseUrl: string = '';
  public siteBaseUrl: string = '';
  public publicProfileUrl: string = '';
  public hasStarted: boolean = false;
  public totalTopics: number = 0;
  public totalCompletedTopics: number = 0;
  public categories = new Array<Category>();
  public userDashboard = new UserDashboard();
  public usercourses = new Array<UserCourse>();
  public team = new Team();
  public teamMembers = new Array<TeamMember>();
  public summaryObject = new Array<SummaryObject>();
  public introText = new Array<ContentObject>();

  private _isElevateUserSubject = new BehaviorSubject<boolean | null>(null);

  public HttpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  constructor(
    private http: HttpClient,
    private applicationInsightsService: ApplicationInsightsService,
  ) {}

  public getUserDetails(cacheBuster: boolean = false): Observable<UserDetails> {
    if (cacheBuster) {
      sessionStorage.removeItem('UserDetails');
      return this.http.get<UserDetails>('api/user/details').pipe(
        tap((data: UserDetails) => {
          this.populateUserVariables(data);
        }),
      );
    }

    if (sessionStorage.getItem('UserDetails')) {
      let data = JSON.parse(sessionStorage.getItem('UserDetails') || '{}');
      this.populateUserVariables(data);
      return of(data);
    } else {
      return this.http.get<UserDetails>('api/user/details').pipe(
        tap((data: UserDetails) => {
          this.populateUserVariables(data);
        }),
        catchError(this.handleError as any),
      );
    }
  }

  public getHasStartedStatus(): Observable<boolean> {
    return this.getUserDetails().pipe(
      concatMap((userDetail) => {
        if (userDetail.hasStarted) return of(true);

        return this.http.get<boolean>('api/user/hasstarted').pipe(
          map((status) => {
            userDetail.hasStarted = status;
            this.populateUserVariables(userDetail);
            return status;
          }),
        );
      }),
    );
  }

  private populateUserVariables(data: UserDetails): void {
    this.firstName = data.firstName;
    this.lastName = data.lastName;
    this.personId = data.personId;
    this.initials = this.firstName.charAt(0).toUpperCase() + this.lastName.charAt(0).toUpperCase();
    this.emailAddress = data.emailAddress;
    this.mbtiType = data.mbtiType;
    this.assessmentSiteBaseUrl = data.assessmentSiteBaseUrl;
    this.marketingSiteBaseUrl = data.marketingSiteBaseUrl;
    this.loginSiteBaseUrl = data.loginSiteBaseUrl;
    this.siteBaseUrl = data.siteBaseUrl;
    this.publicProfileUrl = data.publicProfileUrl;
    this.hasStarted = data.hasStarted;
    this._isElevateUserSubject.next(data.isElevateUser);

    // remove personId from UserDetails and store it as p
    if (data.personId) {
      sessionStorage.setItem('p', data.personId.toString());
      data.personId = Guid.createEmpty();
    }

    sessionStorage.setItem('UserDetails', JSON.stringify(data));
  }

  /**
   * Observable that emits whether the current user is an elevate user or not.
   * Used as a flag to display certain content.
   */
  get isElevateUser$(): Observable<boolean | null> {
    return this._isElevateUserSubject.asObservable();
  }

  public isShowingEmptyState(): boolean {
    return (this.getCurrentTeamMember().isTeamLeader && this.mbtiType === '') || this.mbtiType === '----';
  }

  public getCurrentTeamMember(): TeamMember {
    return this.getTeamInfo().teamMembers.find((obj) => {
      return obj.currentUser === true;
    }) as TeamMember;
  }

  public getTeamInfo(): Team {
    return $.extend({}, this.team);
  }

  public getTeamMembers(): Array<TeamMember> {
    return this.team.teamMembers;
  }

  public getTeamMembersWithoutLeader(): Array<TeamMember> {
    return this.team.teamMembers.filter((tm) => !tm.isTeamLeader);
  }

  public setTeam(data: Team): void {
    this.team = data;
  }

  private getEmptyDashboard(): Observable<Category[]> {
    // Cache content in sessionStorage so we don't hit the API on subsequent trips
    if (sessionStorage.getItem('Dashboard')) {
      let data = JSON.parse(sessionStorage.getItem('Dashboard') || '{}');
      return of(data);
    } else {
      return this.http.get<Category[]>('/api/course/initializeDashboard').pipe(
        tap((data) => {
          sessionStorage.setItem('Dashboard', JSON.stringify(data));
        }),
        catchError(this.handleError),
      );
    }
  }

  private getDashboardWithUserStatus(): Observable<Category[]> {
    // If the empty dashboard is in the browser session cache, pass it as a parameter into the api
    if (sessionStorage.getItem('Dashboard')) {
      return this.http
        .post<Category[]>('/api/course/dashboard', JSON.parse(sessionStorage.getItem('Dashboard') || '{}'))
        .pipe(catchError(this.handleError));
    } else {
      // If the empty dashboard isn't in the browser session cache, get it first
      return this.http.get<Category[]>('/api/course/initializeDashboard').pipe(
        switchMap((data) => {
          sessionStorage.setItem('Dashboard', JSON.stringify(data));
          return this.http
            .post<Category[]>('/api/course/dashboard', JSON.parse(sessionStorage.getItem('Dashboard') || '{}'))
            .pipe(catchError(this.handleError));
        }),
        catchError(this.handleError),
      );
    }
  }

  public getDashboard(): Observable<Category[]> {
    return this.getDashboardWithUserStatus().pipe(
      tap((resultArray) => {
        this.categories = resultArray;
        this.categories.sort((leftSide, rightSide): number => {
          if (leftSide.isUserSelected > rightSide.isUserSelected) return -1;
          if (leftSide.isUserSelected < rightSide.isUserSelected) return 1;
          return 0;
        });
        this.setCategories(this.categories);

        let lUserDashboard = new UserDashboard();
        let totTopics = 0;
        let totCompletedTopics = 0;

        for (var category of this.categories) {
          for (var course of category.courses) {
            var userCourse = new UserCourse();
            userCourse.courseId = course.courseId;
            userCourse.courseName = course.courseName;
            userCourse.isCompleted = course.isCompleted;
            userCourse.allowCompare = course.allowCompare;
            lUserDashboard.userCourses.push(userCourse);
            totTopics += course.numTopics;
            totCompletedTopics += course.numCompletedTopics;
          }
        }
        this.setTotalTopics(totTopics);
        this.setTotalCompletedTopics(totCompletedTopics);
        this.setUserDashboard(lUserDashboard);
      }),
      catchError(this.handleError),
    );
  }

  public getUserDashboard(): UserDashboard {
    return this.userDashboard;
  }

  public setUserDashboard(data: UserDashboard): void {
    this.userDashboard = data;
  }

  public getCategories(): Category[] {
    return this.categories;
  }

  public setCategories(data: Category[]): void {
    this.categories = data;
  }

  public setTotalTopics(data: number): void {
    this.totalTopics = data;
  }

  public getTotalTopics(): number {
    return this.totalTopics;
  }

  public setTotalCompletedTopics(data: number): void {
    this.totalCompletedTopics = data;
  }

  public getTotalCompletedTopics(): number {
    return this.totalCompletedTopics;
  }

  public getContentForCompare(subject: string, compareType: string, compareToType: string, compareTense: number = 0): Observable<string[]> {
    // Cache content in sessionStorage so we don't hit the API on subsequent trips
    let cacheKey = `Compare.${subject}.${compareType}.${compareToType}.${compareTense}`;

    if (sessionStorage.getItem(cacheKey)) {
      let data = JSON.parse(sessionStorage.getItem(cacheKey) || '{}');
      return of(data);
    } else {
      return this.http.get<string[]>('api/course/compare/' + subject + '/' + compareType + '/' + compareToType + '/' + compareTense).pipe(
        tap((data) => {
          sessionStorage.setItem(cacheKey, JSON.stringify(data));
        }),
        catchError(this.handleError),
      );
    }
  }

  public getMbtiTypes(): string[] {
    return ['ISTJ', 'ISFJ', 'INFJ', 'INTJ', 'ISTP', 'ISFP', 'INFP', 'INTP', 'ESTP', 'ESFP', 'ENFP', 'ENTP', 'ESTJ', 'ESFJ', 'ENFJ', 'ENTJ'];
  }

  public loadScript(url: string, deferScript: boolean = true) {
    const body = <HTMLDivElement>document.body;
    const script = document.createElement('script');
    script.innerHTML = '';
    script.src = url;
    script.async = false;
    script.defer = deferScript;
    body.appendChild(script);
  }

  public initializeDashboard(): Observable<Category[]> {
    return this.getEmptyDashboard().pipe(
      tap((resultArray) => {
        this.categories = resultArray;
        this.categories.sort((leftSide, rightSide): number => {
          if (leftSide.isUserSelected > rightSide.isUserSelected) return -1;
          if (leftSide.isUserSelected < rightSide.isUserSelected) return 1;
          return 0;
        });
        this.setCategories(this.categories);

        let lUserDashboard = new UserDashboard();
        let totTopics = 0;
        let totCompletedTopics = 0;

        for (var category of this.categories) {
          for (var course of category.courses) {
            var userCourse = new UserCourse();
            userCourse.courseId = course.courseId;
            userCourse.courseName = course.courseName;
            userCourse.isCompleted = course.isCompleted;
            userCourse.allowCompare = course.allowCompare;
            lUserDashboard.userCourses.push(userCourse);
            totTopics += course.numTopics;
            totCompletedTopics += course.numCompletedTopics;
          }
        }
        this.setTotalTopics(totTopics);
        this.setTotalCompletedTopics(totCompletedTopics);
        this.setUserDashboard(lUserDashboard);
      }),
      catchError(this.handleError),
    );
  }

  public initializeTeam(): Observable<Team> {
    return this.http.get<Team>('api/teams/get').pipe(
      tap((resultArray) => {
        let _team = new Team();
        let _teamMembers = new Array<TeamMember>();

        _team.teamId = resultArray.teamId;
        _team.teamName = resultArray.teamName;
        _team.teamMbtiType = resultArray.teamMbtiType;
        _team.teamCommunicationStyles = resultArray.teamCommunicationStyles;
        _team.isElevateTeam = resultArray.isElevateTeam;
        _team.redirectUrl = resultArray.redirectUrl;
        _team.linkBackUrl = resultArray.linkBackUrl;

        resultArray.teamMembers.forEach((teamMember) => {
          let _tm = new TeamMember();

          _tm.teamId = _team.teamId;
          _tm.teamMemberId = teamMember.teamMemberId;
          _tm.personId = teamMember.personId;
          _tm.emailAddress = teamMember.emailAddress;
          _tm.firstName = teamMember.firstName;
          _tm.lastName = teamMember.lastName;
          _tm.initials = teamMember.firstName.charAt(0).toUpperCase() + teamMember.lastName.charAt(0).toUpperCase();
          _tm.mbtiType = teamMember.mbtiType;
          _tm.giftCode = teamMember.giftCode;
          _tm.isImported = teamMember.isImported;
          _tm.isTeamLeader = teamMember.isTeamLeader;
          _tm.inviteDate = teamMember.inviteDate;
          _tm.isRegistered = teamMember.isRegistered;
          _tm.consentToShareType = !!teamMember.consentToShareType;
          _tm.isElevateTeam = _team.isElevateTeam;

          _teamMembers.push(_tm);
        });

        if (!this.emailAddress) {
          if (environment.environmentName === 'Local') {
            console.error('Error: Unable to get email address of current user.  Refreshing the page...');
            window.location.reload();
          } else {
            throw 'Error: Unable to get email address of current user.  Please refresh the page.';
          }
        } else if (_teamMembers.some((obj) => obj.emailAddress === this.emailAddress)) {
          // Set currently logged in user in the team array
          const currentUser = _teamMembers.find((obj) => obj.emailAddress === this.emailAddress);
          if (currentUser) {
            currentUser.currentUser = true;
          }
        } else {
          throw `Email: ${this.emailAddress} not found in Team: ${_team.teamName} {${_team.teamId}}`;
        }

        // Sort alphabetically by currentUser true (1), then firstname
        _teamMembers.sort(
          (a, b) => Number(b.currentUser) - Number(a.currentUser) || a.firstName.localeCompare(b.firstName) || a.lastName.localeCompare(b.lastName),
        );

        // Now populate the memberId and colors
        let colors = ['#00C5C7', '#E35A41', '#FABB2A', '#5D40B0'];
        let memberId = 0;

        _teamMembers.forEach((tm) => {
          if (tm.mbtiType) {
            tm.color = colors[memberId % colors.length];
            tm.memberId = memberId;
            memberId++;
          } else {
            tm.memberId = -1;
            tm.color = '#929694';
          }
        });

        _team.teamMembers = _teamMembers;
        this.setTeam(_team);
      }),
      catchError(this.handleError),
    );
  }

  public getSummaryContent(mbtiType: string): Observable<SummaryObject[]> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      params: {
        ReportCode: 'MbtiTypeDescriptions',
        MBTIVerifiedType: mbtiType,
      },
    };

    // Cache content in sessionStorage so we don't hit the API on subsequent trips
    if (sessionStorage.getItem(httpOptions.params.ReportCode)) {
      let data = JSON.parse(sessionStorage.getItem(httpOptions.params.ReportCode) || '{}');
      this.summaryObject = data;
      return of(data);
    } else {
      return this.http.get<SummaryObject[]>('/api/report/content', httpOptions).pipe(
        tap((data) => {
          sessionStorage.setItem(httpOptions.params.ReportCode, JSON.stringify(data));
          this.summaryObject = data;
        }),
        catchError(this.handleError),
      );
    }
  }

  public getIntroContent(): Observable<ContentObject[]> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      params: {
        ReportCode: 'TeamsIntroCopy',
      },
    };

    // Cache content in sessionStorage so we don't hit the API on subsequent trips
    if (sessionStorage.getItem(httpOptions.params.ReportCode)) {
      let data = JSON.parse(sessionStorage.getItem(httpOptions.params.ReportCode) || '{}');
      this.introText = data;
      return of(data);
    } else {
      return this.http.get<ContentObject[]>('/api/report/content', httpOptions).pipe(
        tap((data) => {
          sessionStorage.setItem(httpOptions.params.ReportCode, JSON.stringify(data));
          this.introText = data;
        }),
        catchError(this.handleError),
      );
    }
  }

  public getCourse(courseId: Guid): Observable<Course> {
    // Cache content in sessionStorage so we don't hit the API on subsequent trips
    let cacheKey = `Course.${courseId}`;

    if (sessionStorage.getItem(cacheKey)) {
      let data = JSON.parse(sessionStorage.getItem(cacheKey) || '{}');
      return of(data);
    } else {
      return this.http.get<Course>('/api/course?courseId=' + courseId).pipe(
        tap((data) => {
          sessionStorage.setItem(cacheKey, JSON.stringify(data));
        }),
        catchError(this.handleError),
      );
    }
  }

  public handleError(exception: any): Observable<never> {
    if (exception instanceof HttpErrorResponse && exception.status === 401) {
      //var target = window.location.pathname;
      //window.location.href = "/?target=" + encodeURIComponent(target);
      return throwError(() => new Error('Unauthorized access'));
    } else {
      // Log error in App Insights if we can connect to it
      if (typeof this.applicationInsightsService !== 'undefined') this.applicationInsightsService.logException(exception);

      if (environment.production) return throwError(() => new Error(exception.message));
      else return throwError(() => new Error(exception));
    }
  }

  public handleErrorWithRedirect(exception: any) {
    if (exception instanceof HttpErrorResponse && exception.status === 401) {
      //var target = window.location.pathname;
      //window.location.href = "/?target=" + encodeURIComponent(target);
    } else {
      // Log error in App Insights if we can connect to it
      if (typeof this.applicationInsightsService !== 'undefined') this.applicationInsightsService.logException(exception);

      window.location.href = '/error.html';
    }
  }
}
